/**
 * Pipeline Permission Utilities
 *
 * This module provides utilities for checking pipeline access based on
 * role-based, user-based, and team-based permissions set on individual pipelines.
 */

interface Pipeline {
  id: number;
  name: string;
  pipeline_type: 'commercial' | 'residential' | 'onboarding';
  role_permissions?: string[];
  user_permissions?: string[];
  team_permissions?: string[];
}

interface UserInfo {
  id: string;
  role: string;
  teamId?: string;
}

/**
 * Checks if a user has access to a specific pipeline
 *
 * @param pipeline - The pipeline to check access for
 * @param userInfo - User information (id, role, teamId)
 * @param hasSystemOverride - Whether user has system-level override permissions
 * @returns boolean - true if user has access, false otherwise
 */
export const checkPipelineAccess = (
  pipeline: Pipeline,
  userInfo: UserInfo,
  hasSystemOverride: boolean = false
): boolean => {
  const { id: userId, role: userRole, teamId: userTeamId } = userInfo;

  // Superadmins and Admins can see everything
  if (hasSystemOverride || userRole === 'Admin' || userRole === 'Superadmin') {
    return true;
  }

  // If no restrictions are set on the pipeline, allow access based on general permissions
  // This means the pipeline uses the default permission system (view_commercial_pipeline, etc.)
  if ((!pipeline.role_permissions || pipeline.role_permissions.length === 0) &&
      (!pipeline.user_permissions || pipeline.user_permissions.length === 0) &&
      (!pipeline.team_permissions || pipeline.team_permissions.length === 0)) {
    return true; // No restrictions, defer to general pipeline permissions
  }

  // Check role-based permissions
  if (pipeline.role_permissions && pipeline.role_permissions.includes(userRole)) {
    return true;
  }

  // Check user-specific permissions
  if (pipeline.user_permissions && pipeline.user_permissions.includes(userId)) {
    return true;
  }

  // Check team-based permissions (if team system is implemented)
  if (pipeline.team_permissions && userTeamId && pipeline.team_permissions.includes(userTeamId)) {
    return true;
  }

  // If pipeline has restrictions but user doesn't match any, deny access
  return false;
};

/**
 * Filters an array of pipelines based on user access permissions
 *
 * @param pipelines - Array of pipelines to filter
 * @param userInfo - User information (id, role, teamId)
 * @param hasSystemOverride - Whether user has system-level override permissions
 * @returns Pipeline[] - Filtered array of pipelines the user can access
 */
export const filterPipelinesByAccess = (
  pipelines: Pipeline[],
  userInfo: UserInfo,
  hasSystemOverride: boolean = false
): Pipeline[] => {
  return pipelines.filter(pipeline =>
    checkPipelineAccess(pipeline, userInfo, hasSystemOverride)
  );
};

/**
 * Checks if a user has general permission to view pipelines of a certain type
 * This is used as a fallback when no specific pipeline restrictions exist
 *
 * @param pipelineType - The type of pipeline ('commercial', 'residential', 'onboarding')
 * @param permissions - Set of user permissions
 * @returns boolean - true if user has general permission for this pipeline type
 */
export const hasGeneralPipelinePermission = (
  pipelineType: 'commercial' | 'residential' | 'onboarding',
  permissions: Set<string>
): boolean => {
  switch (pipelineType) {
    case 'commercial':
      return permissions.has('view_commercial_pipeline');
    case 'residential':
      return permissions.has('view_residential_pipeline');
    case 'onboarding':
      return permissions.has('view_onboarding_pipeline');
    default:
      return false;
  }
};